import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Save, Visibility, VisibilityOff, } from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import Header from "../../components/Header";
import UserService from "../../api/services/UserService";

// Styles personnalisés
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha("#2a5fa9", 0.1)} 20%, ${alpha("#4cbed8", 0.1)} 40%, ${alpha("#65b986", 0.1)} 60%, ${alpha("#8fc68b", 0.1)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: "24px",
  padding: theme.spacing(4),
  maxWidth: "800px",
  width: "100%",
  boxShadow: `0 10px 30px ${alpha("#2a5fa9", 0.2)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 20px 40px ${alpha("#2a5fa9", 0.3)}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "16px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: alpha(theme.palette.background.paper, 0.95),
    "& fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2a5fa9",
    },
  },
  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.text.primary, 0.7),
    "&.Mui-focused": {
      color: "#2a5fa9",
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
  color: "#ffffff",
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  "&:hover": {
    background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
    transform: "scale(1.05)",
  },
  "&:disabled": {
    background: alpha(theme.palette.action.disabled, 0.3),
    color: theme.palette.action.disabled,
  },
  transition: "all 0.2s ease",
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#2a5fa9",
    "&:hover": {
      backgroundColor: alpha("#2a5fa9", 0.1),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#4cbed8",
  },
  "& .MuiSwitch-track": {
    backgroundColor: alpha(theme.palette.grey[500], 0.3),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
  fontSize: "1.5rem",
  marginBottom: theme.spacing(2),
}));

const EditProfil = () => {
  const [userData, setUserData] = useState({
    nom: "",
    email: "",
    mot_de_passe_h: "",
    current_password: "",
    confirm_password: "",
    deux_fa_active: false,
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const togglePassword = (field) => () => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");
  const userRole = localStorage.getItem("user_role");

  useEffect(() => {
    const storedUserData = JSON.parse(localStorage.getItem("user_data")) || {};
    setUserData((prev) => ({
      ...prev,
      nom: storedUserData.nom || "",
      email: storedUserData.email || "",
      deux_fa_active: storedUserData.deux_fa_active === 1 || false,
    }));

    const fetchUserData = async () => {
      if (!token || !userId || !["gestionnaire", "admin"].includes(userRole)) {
        setError("Utilisateur non authentifié ou rôle incorrect.");
        return;
      }

      try {
        const response = await UserService.getProfile(userId, token, userRole);
        setUserData((prev) => ({
          ...prev,
          nom: response.nom || "",
          email: response.email || "",
          deux_fa_active: response.deux_fa_active === 1,
        }));
        setError("");
      } catch (err) {
        setError(err || "Erreur lors de la récupération des données du profil.");
      }
    };

    fetchUserData();
  }, [userId, token, userRole]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!userData.nom?.trim()) return setError("Le nom est requis.");
    if (!userData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email))
      return setError("Email invalide.");

    if (userData.mot_de_passe_h) {
      if (userData.mot_de_passe_h.length < 6)
        return setError("Le mot de passe doit faire au moins 6 caractères.");
      if (userData.mot_de_passe_h !== userData.confirm_password)
        return setError("Les mots de passe ne correspondent pas.");
    }

    try {
      const updateData = {
        nom: userData.nom,
        email: userData.email,
        deux_fa_active: userData.deux_fa_active ? 1 : 0,
      };

      if (userData.mot_de_passe_h) {
        updateData.mot_de_passe_h = userData.mot_de_passe_h;
        updateData.mot_de_passe_h_confirmation = userData.confirm_password;
      }

      await UserService.updateProfile(userId, updateData, token, userRole);

      // CORRECTION: Récupérer les anciennes données pour conserver la société et autres infos
      const oldUserData = JSON.parse(localStorage.getItem("user_data")) || {};
      
      localStorage.setItem("user_data", JSON.stringify({
        ...oldUserData, // Conserver toutes les anciennes données (societe, id, etc.)
        nom: userData.nom,
        email: userData.email,
        deux_fa_active: userData.deux_fa_active ? 1 : 0,
      }));

      setUserData(prev => ({
        ...prev,
        mot_de_passe_h: "",
        confirm_password: "",
      }));

      setSuccess("Profil mis à jour avec succès !");
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(err?.response?.data?.message || "Erreur lors de la mise à jour.");
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      minHeight="auto"
      sx={{
        background: "#ffffff",
        padding: { xs: 1, sm: 2, md: 2 },
        marginTop: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <StyledPaper elevation={4}>
        <Header
          title={<SectionTitle>Gestion du Profil</SectionTitle>}
          subtitle="Modifier vos informations personnelles"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: "12px" }}>
            {success}
          </Alert>
        )}

        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <StyledTextField
            label="Nom"
            name="nom"
            value={userData.nom}
            onChange={handleChange}
            fullWidth
            required
            variant="outlined"
          />
          <StyledTextField
            label="Email"
            name="email"
            type="email"
            value={userData.email}
            onChange={handleChange}
            fullWidth
            required
            variant="outlined"
          />
          {/* <StyledTextField
            label="Mot de passe actuel"
            name="current_password"
            type={showPassword.current ? "text" : "password"}
            // value={userData.current_password}
            onChange={handleChange}
            fullWidth
            helperText="Requis uniquement si vous modifiez le mot de passe"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePassword("current")} edge="end">
                    {showPassword.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          /> */}
          <StyledTextField
            label="Nouveau mot de passe"
            name="mot_de_passe_h"
            type={showPassword.new ? "text" : "password"}
            value={userData.mot_de_passe_h}
            onChange={handleChange}
            fullWidth
            helperText=""
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePassword("new")} edge="end">
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <StyledTextField
            label="Confirmer le nouveau mot de passe"
            name="confirm_password"
            type={showPassword.confirm ? "text" : "password"}
            value={userData.confirm_password}
            onChange={handleChange}
            fullWidth
            helperText=""
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePassword("confirm")} edge="end">
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
         
          <StyledButton
            type="submit"
            variant="contained"
            startIcon={<Save />}
            sx={{
              borderRadius: '8px',
              background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
              color: '#ffffff',
              '&:hover': {
                background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                transform: 'scale(1.05)'
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Mettre à jour
          </StyledButton>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default EditProfil;